import type { CSSProperties } from 'react';
import { usePropertyField } from '@vibesites/runtime/react';

export interface Color4 {
  /** Normalized `#rrggbb`. */
  color: string;
  /** Black or white, whichever reads better on `color`. */
  textOn: string;
  /**
   * Remaps the primary colour variables to Color 4. Spread onto a CTA (or a
   * block wrapper) so anything inside styled with `--vibe-color-primary`
   * picks up Color 4 instead.
   */
  primaryOverride: CSSProperties;
}

export function parseHexColor(raw: unknown): [number, number, number] | null {
  if (typeof raw !== 'string') return null;
  const m = raw.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  const hex = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}

function readableTextOn([r, g, b]: [number, number, number]): string {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.179 ? '#000000' : '#ffffff';
}

/** The property's Color 4 (`property_color_4`), or null when unset / not a hex colour. */
export function useColor4(): Color4 | null {
  const rgb = parseHexColor(usePropertyField('property_color_4'));
  if (!rgb) return null;
  const color = `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
  const textOn = readableTextOn(rgb);
  return {
    color,
    textOn,
    primaryOverride: {
      '--vibe-color-primary': color,
      '--vibe-color-text-on-primary': textOn,
    } as CSSProperties,
  };
}
